"use client";

import {
  FeedEvents,
  FollowComedianEvents,
  ShowsNearbyEvents,
  trackEvent,
} from "@punchup/analytics";
import { useCallback, useState, useTransition } from "react";

import { AllFiltersButton } from "./AllFiltersButton";
import { CheckoutModalOrDrawer } from "@punchup/ui-common/components/checkout";
import { Comedian } from "@/data/comedians";
import { FOLLOW_LOCATIONS } from "@/data/follows";
import { ShowsFiltersModalOrDrawer } from "./ShowsFiltersModalOrDrawer";
import { ToggleFilterButton } from "./ToggleFilterButton";
import { useGate } from "statsig-react";

export type DateFilterOption = "all" | "thisWeekend" | "thisWeek" | "thisMonth";
export type ComedianFilterOption = "all" | "otherComedians" | "myComedians";
export type CheckoutFilterOption = "all" | "punchupVenues" | "externalCheckout";
export type FilterOption =
  | DateFilterOption
  | ComedianFilterOption
  | CheckoutFilterOption;

export interface FilterState {
  dateFilter: DateFilterOption;
  comedianFilter: ComedianFilterOption;
  checkoutFilter: CheckoutFilterOption;
}

interface ShowsNearbyFiltersProps {
  filterState: FilterState;
  onFilterChange: (newState: FilterState) => void;
  followedComedianIds: string[];
  isLoggedIn?: boolean;
  defaultComedianToFollow?: Pick<
    Comedian,
    "id" | "display_name" | "display_name_short" | "slug" | "about_img"
  >;
  isLoading?: boolean;
  radius: number;
  openLocationModal: () => void;
}

// Default comedian to use when we don't have a specific one
const DEFAULT_COMEDIAN_TO_FOLLOW: Pick<
  Comedian,
  "id" | "display_name" | "display_name_short" | "slug" | "about_img"
> = {
  id: "default",
  display_name: "Punchup",
  display_name_short: "Punchup",
  slug: "punchup",
  about_img: null,
};

export default function ShowsNearbyFilters({
  filterState,
  onFilterChange,
  followedComedianIds,
  isLoggedIn = false,
  defaultComedianToFollow = DEFAULT_COMEDIAN_TO_FOLLOW,
  isLoading = false,
  radius,
  openLocationModal,
}: ShowsNearbyFiltersProps) {
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [optimisticState, setOptimisticState] =
    useState<FilterState>(filterState);
  const [isPending, startTransition] = useTransition();
  const hasFollowedComedians = followedComedianIds.length > 0;

  const { value: radiusButtonEnabled } = useGate(
    "shows_nearby_miles_radius_filter_button"
  );

  const handleDateFilter = useCallback(
    (filter: DateFilterOption) => {
      if (filter === optimisticState.dateFilter) {
        // If clicking the active date filter, do nothing
        return;
      }

      // Update optimistic state immediately
      const newState = {
        ...optimisticState,
        dateFilter: filter,
      };
      setOptimisticState(newState);

      // Track an event
      trackEvent(ShowsNearbyEvents.FILTER_DATE, {
        DateFilter: filter,
      });

      // Then update actual state
      startTransition(() => {
        onFilterChange(newState);
      });
    },
    [optimisticState, onFilterChange]
  );

  const handleComedianFilter = useCallback(
    (filter: ComedianFilterOption) => {
      if (!isLoggedIn) {
        // Show sign in modal if not logged in
        trackEvent(FollowComedianEvents.SHOWS_NEARBY, {
          followLocation: FOLLOW_LOCATIONS.SHOWS_NEARBY,
          comedianId: defaultComedianToFollow?.id,
          comedianName: defaultComedianToFollow?.display_name,
        });
        setShowSignInModal(true);
        return;
      }

      if (filter === optimisticState.comedianFilter) {
        // If clicking the active comedian filter, do nothing
        return;
      }

      // Update optimistic state immediately
      const newState = {
        ...optimisticState,
        comedianFilter: filter,
      };
      setOptimisticState(newState);

      // Track an event
      if (filter === "all") {
        trackEvent(ShowsNearbyEvents.FILTER_ALL_COMEDIANS, {
          ComedianFilter: filter,
        });
      } else if (filter === "myComedians") {
        trackEvent(ShowsNearbyEvents.FILTER_MY_COMEDIANS, {
          ComedianFilter: filter,
        });
      } else if (filter === "otherComedians") {
        trackEvent(ShowsNearbyEvents.FILTER_OTHER_COMEDIANS, {
          ComedianFilter: filter,
        });
      }

      // Then update actual state
      startTransition(() => {
        onFilterChange(newState);
      });
    },
    [optimisticState, onFilterChange, isLoggedIn, defaultComedianToFollow]
  );

  const handleCheckoutFilter = useCallback(
    (filter: CheckoutFilterOption) => {
      if (filter === optimisticState.checkoutFilter) {
        // If clicking the active date filter, do nothing
        return;
      }

      // Update optimistic state immediately
      const newState = {
        ...optimisticState,
        checkoutFilter: filter,
      };
      setOptimisticState(newState);

      // Track an event
      if (filter === "all") {
        trackEvent(ShowsNearbyEvents.FILTER_ALL_CHECKOUT_TYPES, {
          CheckoutFilter: filter,
        });
      } else if (filter === "externalCheckout") {
        trackEvent(ShowsNearbyEvents.FILTER_EXTERNAL_CHECKOUT, {
          ComedianFilter: filter,
        });
      } else if (filter === "punchupVenues") {
        trackEvent(ShowsNearbyEvents.FILTER_FAST_CHECKOUT, {
          ComedianFilter: filter,
        });
      }

      // Then update actual state
      startTransition(() => {
        onFilterChange(newState);
      });
    },
    [optimisticState, onFilterChange]
  );

  const handleResetFilters = useCallback(() => {
    // set each filter state to "all"
    onFilterChange({
      dateFilter: "all",
      comedianFilter: "all",
      checkoutFilter: "all",
    });
    setOptimisticState({
      dateFilter: "all",
      comedianFilter: "all",
      checkoutFilter: "all",
    });
  }, [onFilterChange]);

  return (
    <>
      <div className="flex overflow-x-auto whitespace-nowrap gap-2 hide-scrollbar px-4 md:px-6 lg:px-2 w-full md:max-w-[1100px] md:mx-auto">
        <AllFiltersButton
          optimisticState={optimisticState}
          onClick={() => setShowFiltersModal(true)}
          disabled={isLoading}
        />
        <ToggleFilterButton
          label="My Comedians"
          disabled={isLoading || (isLoggedIn && !hasFollowedComedians)}
          isSelected={optimisticState.comedianFilter === "myComedians"}
          isPending={isPending}
          onClick={() => {
            const isSelected = optimisticState.comedianFilter === "myComedians";
            if (isSelected) {
              handleComedianFilter("all");
            } else {
              handleComedianFilter("myComedians");
            }
          }}
        />
        {radiusButtonEnabled && (
          <ToggleFilterButton
            label={`Within ${radius} miles`}
            disabled={isLoading}
            isSelected={false}
            isPending={false}
            onClick={openLocationModal}
          />
        )}
        <ToggleFilterButton
          label="Fast Checkout"
          disabled={isLoading}
          isSelected={optimisticState.checkoutFilter === "punchupVenues"}
          isPending={isPending}
          onClick={() => {
            const isSelected =
              optimisticState.checkoutFilter === "punchupVenues";
            if (isSelected) {
              handleCheckoutFilter("all");
            } else {
              handleCheckoutFilter("punchupVenues");
            }
          }}
        />
        <ToggleFilterButton
          label="This Week"
          disabled={isLoading}
          isSelected={optimisticState.dateFilter === "thisWeek"}
          isPending={isPending}
          onClick={() => {
            const isSelected = optimisticState.dateFilter === "thisWeek";
            if (isSelected) {
              handleDateFilter("all");
            } else {
              handleDateFilter("thisWeek");
            }
          }}
        />
        <ToggleFilterButton
          label="This Weekend"
          disabled={isLoading}
          isSelected={optimisticState.dateFilter === "thisWeekend"}
          isPending={isPending}
          onClick={() => {
            const isSelected = optimisticState.dateFilter === "thisWeekend";
            if (isSelected) {
              handleDateFilter("all");
            } else {
              handleDateFilter("thisWeekend");
            }
          }}
        />
        <ToggleFilterButton
          label="This Month"
          disabled={isLoading}
          isSelected={optimisticState.dateFilter === "thisMonth"}
          isPending={isPending}
          onClick={() => {
            const isSelected = optimisticState.dateFilter === "thisMonth";
            if (isSelected) {
              handleDateFilter("all");
            } else {
              handleDateFilter("thisMonth");
            }
          }}
        />
      </div>

      {/* Sign in modal for unauthenticated users */}
      {showSignInModal && (
        <CheckoutModalOrDrawer
          isOpen={showSignInModal}
          setIsOpen={setShowSignInModal}
          comedianToFollow={defaultComedianToFollow as Comedian}
          redirectTo="/nearby"
          followLocation={FOLLOW_LOCATIONS.SHOWS_NEARBY}
          onOpen={(params) => {
            trackEvent(FeedEvents.OPENED_SUBSCRIPTION_MODAL, params);
          }}
        />
      )}
      <ShowsFiltersModalOrDrawer
        isOpen={showFiltersModal}
        isPending={isPending}
        setIsOpen={setShowFiltersModal}
        filterState={filterState}
        optimisticState={optimisticState}
        resetFilters={handleResetFilters}
        handleDateFilter={handleDateFilter}
        handleComedianFilter={handleComedianFilter}
        handleCheckoutFilter={handleCheckoutFilter}
      />
    </>
  );
}
